import {
  booleanAttribute,
  computed,
  Directive,
  effect,
  ElementRef,
  inject,
  input,
  numberAttribute,
  OnDestroy,
  OnInit,
  signal,
  Signal
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MuiStickyService } from './mui-sticky.service';


/**
 * Directive to make an element sticky. Sticky elements will stick to the top of the viewport when scrolled to. In contrast to
 * normal CSS sticky behavior, this directive allows for dynamic offsets and stacking of elements.
 *
 * Applies the `mui-sticky` class to the host element. Applies the `mui-sticky--stuck` class when the element is stuck to the top.
 *
 * An additional offset can be applied to the sticky element using the `offset` input.
 */
@Directive({
  selector: '[muiSticky]',
  exportAs: 'muiSticky',
  standalone: false,
  host: {
    /* eslint-disable @typescript-eslint/naming-convention */
    '[class.mui-sticky]': 'true',
    '[class.mui-sticky--stuck]': 'stuck()',
    '[class.mui-sticky--animated]': 'animated()',
    '[style.top.px]': 'top()'
    /* eslint-enable @typescript-eslint/naming-convention */
  }
})
export class MuiStickyDirective implements OnInit, OnDestroy {

  readonly top: Signal<number>;
  readonly stuck = signal(false);
  readonly stack = input(false, { transform: booleanAttribute });
  readonly animated = input(false, { transform: booleanAttribute });
  readonly offset = input(0, { transform: numberAttribute });
  readonly host = inject<ElementRef<HTMLElement>>(ElementRef);

  private _rafId: number | null = null;
  private readonly _anchorOffset: Signal<number>;
  private readonly stickyService = inject(MuiStickyService);

  constructor() {
    this._anchorOffset = toSignal(this.stickyService.getOffsetForElement(this.host.nativeElement), { initialValue: 0 });
    this.top = computed(() => this._anchorOffset() + this.offset());

    /* Update the service stack array when the element is stuck or unstuck */
    effect(() => {
      const isStacked = this.stuck() && this.stack();
      if (isStacked) {
        this.stickyService.addToStack(this.host.nativeElement);
      } else {
        this.stickyService.removeFromStack(this.host.nativeElement);
      }
    }, { allowSignalWrites: true }); // Signal writes need to be allowed because the stack is updated in the effect
  }

  ngOnInit(): void {
    this.startAnimationFrameTracking();
  }

  ngOnDestroy(): void {
    this.stickyService.removeFromStack(this.host.nativeElement);

    // Clean up the animation frame tracking
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
    }
  }

  private checkIfStuck(): void {
    const { top } = this.host.nativeElement.getBoundingClientRect();
    const stuck = top === this.top();

    if (this.stuck() !== stuck) {
      this.stuck.set(stuck);
    }
  }

  private startAnimationFrameTracking() {
    const doCheck = () => {
      this.checkIfStuck();
      this._rafId = requestAnimationFrame(doCheck);
    };
    this._rafId = requestAnimationFrame(doCheck);
  }
}



